import { Card, CardContent } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { getAirportDetails } from "@/data/airportDetails";

interface FAQItem {
  question: string;
  answer: string;
}

interface AirportFAQProps {
  airportCode: string;
  airportName: string;
  city: string;
}

export const AirportFAQ = ({ airportCode, airportName, city }: AirportFAQProps) => {
  const details = getAirportDetails(airportCode);
  const cityName = city.split(",")[0];
  
  // Build factual FAQs using real airport data
  const faqs: FAQItem[] = [
    {
      question: `What is the current TSA wait time at ${airportCode}?`,
      answer: `TSA wait times at ${airportName} vary throughout the day. Based on typical patterns, expect ${details?.avgDomesticWait || "15-25 minutes"} during normal hours. Peak times (${details?.peakHours.morning || "5-8 AM"} and ${details?.peakHours.evening || "4-7 PM"}) can see longer waits. Check AirportBuddy for real-time wait times updated every minute.`
    },
    {
      question: `How early should I arrive at ${airportCode} for my flight?`,
      answer: `For domestic flights from ${airportName}, arrive at least 2 hours before departure. For international flights, arrive 3 hours early. During peak travel times (${details?.peakHours.busiestDays?.join(", ") || "Monday, Friday, Sunday"}), add an extra 30-45 minutes. ${details?.preCheckLanes ? "TSA PreCheck members can typically arrive 30 minutes later." : ""}`
    },
    {
      question: `How many terminals does ${airportCode} have?`,
      answer: details 
        ? `${airportName} has ${details.terminals} terminal${details.terminals > 1 ? "s" : ""}: ${details.terminalNames.join(", ")}. The airport serves approximately ${details.annualPassengers} million passengers annually.`
        : `${airportName} serves millions of passengers annually. Check your airline's website for terminal information.`
    },
    {
      question: `Does ${airportCode} have TSA PreCheck and CLEAR?`,
      answer: details
        ? `Yes, ${airportName} offers TSA PreCheck lanes${details.clearLanes ? " and CLEAR lanes" : ""}. TSA PreCheck members typically experience wait times of 5 minutes or less. ${details.clearLanes ? "CLEAR members can bypass the ID verification line entirely." : ""}`
        : `${airportName} offers TSA PreCheck lanes at most checkpoints. Check TSA.gov for enrollment information.`
    },
    {
      question: `What are the busiest times at ${airportCode}?`,
      answer: details
        ? `${airportName} is busiest during morning rush (${details.peakHours.morning}) and evening rush (${details.peakHours.evening}). The busiest days are typically ${details.peakHours.busiestDays.join(", ")}. For the shortest wait times, try flying mid-morning (9-11 AM) or early afternoon (1-3 PM) on Tuesday or Wednesday.`
        : `${airportName} experiences the highest security wait times during early morning (5-8 AM) and evening (4-7 PM) hours. Weekends and holidays are also busy.`
    },
    {
      question: `Which airlines operate at ${airportCode}?`,
      answer: details
        ? `${airportName} is a hub for ${details.hubAirlines.join(" and ")}. The airport is served by most major domestic and international carriers.`
        : `${airportName} is served by most major airlines. Check the airport's official website for a complete list of carriers.`
    },
    {
      question: `How do I get to ${airportCode} from downtown ${cityName}?`,
      answer: details
        ? `You can reach ${airportName} via: ${details.transitOptions.join(", ")}. ${details.transitOptions.some(t => t.toLowerCase().includes("rail") || t.toLowerCase().includes("metro") || t.toLowerCase().includes("line")) ? "Public transit is often the most affordable option." : "Rideshare services are available at designated pickup areas."}`
        : `${airportName} is accessible by public transit, rideshare, taxi, and rental car. Check the airport website for specific transit options.`
    },
    {
      question: `How much does parking cost at ${airportCode}?`,
      answer: details
        ? `Parking at ${airportName}: Economy parking starts at ${details.parking.economy}, garage parking at ${details.parking.garage}${details.parking.valet ? `, and valet parking at ${details.parking.valet}` : ""}. Book in advance for the best rates.`
        : `${airportName} offers economy, garage, and valet parking options. Economy lots are typically the most affordable for multi-day parking.`
    },
    {
      question: `What should I know about security at ${airportCode}?`,
      answer: details
        ? `Security tips for ${airportName}: ${details.tipsSecurity.join(". ")}. Always check current wait times on AirportBuddy before leaving for the airport.`
        : `${airportName} has multiple security checkpoints. Arrive early during peak hours and have your ID and boarding pass ready.`
    },
    {
      question: `Can I track my flight departing from ${airportCode}?`,
      answer: `Yes! AirportBuddy provides real-time flight tracking for ${airportName}. View departure times, gate information, flight status, and delays for all airlines operating at ${airportCode}. Sign in to access live flight data.`
    }
  ];

  return (
    <Card>
      <CardContent className="pt-6">
        <h2 className="text-2xl font-bold text-foreground mb-6">
          Frequently Asked Questions About {airportCode}
        </h2>
        <Accordion type="single" collapsible className="w-full">
          {faqs.map((faq, index) => (
            <AccordionItem key={index} value={`item-${index}`}>
              <AccordionTrigger className="text-left font-semibold text-foreground">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </CardContent>
    </Card>
  );
};
